import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import { useTranslation } from "react-i18next";
import customerServices from "../../../services/CustomerService";
import codService from "../../../services/CodService";
import SearchField from "../../component/common/searchField";
import { showToast } from "../../component/common/toastNotification";
import { CodServedCustomer } from "../../../types/codSalesRep";
import {
  buildCustomerOutstandingLookup,
  formatCodAmount,
  isSalesRepCodeConfigError,
  outstandingLookupFromSummaries,
  resolveCustomerOutstanding,
} from "../../../utils/codUtils";
import { glassCardSx, NJIM } from "./codStyles";
import CodInitialsAvatar from "../../../components/ui/CodInitialsAvatar";
import CardSkeleton from "../../../components/ui/CardSkeleton";

const pickField = (raw: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
};

export const mapServedCustomer = (item: Record<string, unknown>): CodServedCustomer => ({
  id: String(pickField(item, "id", "Id") ?? ""),
  customerCode: String(pickField(item, "customerCode", "CustomerCode") ?? ""),
  customerName: String(
    pickField(item, "customerName", "CustomerName", "title", "Title") ?? ""
  ),
  customerCategory: String(pickField(item, "customerCategory", "CustomerCategory") ?? ""),
  availableCredit: Number(pickField(item, "availableCredit", "AvailableCredit") ?? 0),
  currency: String(pickField(item, "currency", "Currency") ?? "IQD"),
  addressDescription1: String(
    pickField(item, "addressDescription1", "AddressDescription1") ?? ""
  ),
  addressDescription2: String(
    pickField(item, "addressDescription2", "AddressDescription2") ?? ""
  ),
  statGroup3Description: String(
    pickField(item, "statGroup3Description", "StatGroup3Description") ?? ""
  ),
  mobileNumber: String(pickField(item, "mobileNumber", "MobileNumber") ?? ""),
  onlineShipmentSite: String(
    pickField(item, "onlineShipmentSite", "OnlineShipmentSite") ?? ""
  ),
});

const parseSearchItems = (data: unknown): CodServedCustomer[] => {
  const payload = (data ?? {}) as Record<string, unknown>;
  const items = Array.isArray(payload.items)
    ? payload.items
    : Array.isArray(payload.Items)
      ? payload.Items
      : Array.isArray(data)
        ? data
        : [];
  return items.map((row) => mapServedCustomer(row as Record<string, unknown>));
};

type RepCustomerAccess =
  | "loading"
  | "ready"
  | "noneAssigned"
  | "noSalesRepCode"
  | "error";

interface CodCustomerPickerProps {
  selected: CodServedCustomer | null;
  onSelect: (customer: CodServedCustomer | null) => void;
  refreshKey?: number;
  /** UI only: reports the selected customer's outstanding so the page header can show it. */
  onSelectedOutstandingChange?: (amount: number) => void;
}

const CodCustomerPicker: React.FC<CodCustomerPickerProps> = ({
  selected,
  onSelect,
  refreshKey = 0,
  onSelectedOutstandingChange,
}) => {
  const { t } = useTranslation();
  const [repCustomerAccess, setRepCustomerAccess] =
    useState<RepCustomerAccess>("loading");
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerResults, setCustomerResults] = useState<CodServedCustomer[]>([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [debtOnly, setDebtOnly] = useState(false);
  const [outstandingLookup, setOutstandingLookup] = useState<Map<string, number>>(
    () => new Map()
  );

  const searchCustomers = useCallback(
    async (keyword: string) => {
      if (repCustomerAccess === "noSalesRepCode") return;
      setLoadingCustomers(true);
      try {
        const res = await customerServices.searchCustomers(keyword, 50, true);
        const rows = parseSearchItems(res.data);
        setCustomerResults(rows);
        const trimmed = keyword.trim();
        if (!trimmed && rows.length === 0) {
          setRepCustomerAccess("noneAssigned");
        } else {
          setRepCustomerAccess("ready");
        }
      } catch (error: unknown) {
        const err = error as {
          response?: { status?: number; data?: { message?: string } };
        };
        const status = err.response?.status;
        const apiMessage = err.response?.data?.message?.trim() ?? "";
        if (status === 400 && isSalesRepCodeConfigError(apiMessage)) {
          setRepCustomerAccess("noSalesRepCode");
          setCustomerResults([]);
          return;
        }
        setRepCustomerAccess("error");
        showToast(apiMessage || t("codSalesRep.customersLoadFailed"), "error");
        setCustomerResults([]);
      } finally {
        setLoadingCustomers(false);
      }
    },
    [repCustomerAccess, t]
  );

  const loadCustomerOutstanding = useCallback(async () => {
    try {
      const result = await codService.getOutstandingByCustomer();
      setOutstandingLookup(outstandingLookupFromSummaries(result.items));
    } catch {
      try {
        const orders = await codService.listOrders();
        setOutstandingLookup(buildCustomerOutstandingLookup(orders));
      } catch {
        // customer search still works without outstanding
      }
    }
  }, []);

  useEffect(() => {
    loadCustomerOutstanding();
  }, [loadCustomerOutstanding, refreshKey]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (repCustomerAccess !== "noSalesRepCode") {
        searchCustomers(customerSearch);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers, repCustomerAccess]);

  const visibleResults = customerResults.filter((customer) => {
    if (selected?.id === customer.id) return false;
    if (!debtOnly) return true;
    return resolveCustomerOutstanding(outstandingLookup, customer) > 0;
  });

  const customerSearchDisabled = repCustomerAccess === "noSalesRepCode";

  const selectedOutstanding = selected
    ? resolveCustomerOutstanding(outstandingLookup, selected)
    : 0;

  useEffect(() => {
    onSelectedOutstandingChange?.(selectedOutstanding);
  }, [selectedOutstanding, onSelectedOutstandingChange]);

  // Selected customer stays pinned at the top of the list (search results exclude it).
  const listRows = selected && !customerSearch.trim() ? [selected, ...visibleResults] : visibleResults;
  const debtCount = customerResults.filter(
    (customer) => resolveCustomerOutstanding(outstandingLookup, customer) > 0
  ).length;

  const compactAmount = (amount: number) =>
    new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(amount);

  const debtToggle = (
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={debtOnly}
          onChange={(e) => setDebtOnly(e.target.checked)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: NJIM.mint },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: NJIM.mint,
            },
          }}
        />
      }
      label={
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: NJIM.ink }}>
            {t("codSalesRep.debtOnlyFilter")}
          </Typography>
          {debtCount > 0 && (
            <Box component="span" sx={{ ...countDotSx, bgcolor: NJIM.dangerWash, color: NJIM.danger }}>
              {debtCount}
            </Box>
          )}
        </Box>
      }
      sx={{ m: 0, gap: 0.5 }}
    />
  );

  const accessAlerts = (
    <>
      {repCustomerAccess === "noSalesRepCode" && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
          {t("codSalesRep.salesRepCodeNotConfigured")}
        </Alert>
      )}
      {repCustomerAccess === "noneAssigned" && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
          {t("codSalesRep.noCustomersAssigned")}
        </Alert>
      )}
    </>
  );

  const renderRow = (customer: CodServedCustomer) => {
    const isSelected = selected?.id === customer.id;
    const outstanding = resolveCustomerOutstanding(outstandingLookup, customer);
    return (
      <Box component="li" key={customer.id}>
        <Box
          component="button"
          type="button"
          aria-pressed={isSelected}
          onClick={() => (isSelected ? onSelect(null) : onSelect(customer))}
          sx={customerRowSx(isSelected)}
        >
          <CodInitialsAvatar name={customer.customerName} size={34} selected={isSelected} />
          <Box component="span" sx={{ minWidth: 0, flex: 1, textAlign: "start" }}>
            <Typography
              component="span"
              sx={{
                display: "block",
                fontSize: 13,
                fontWeight: isSelected ? 700 : 600,
                color: NJIM.textPrimary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.3,
              }}
              title={customer.customerName}
            >
              {customer.customerName}
            </Typography>
            <Typography
              component="span"
              sx={{ display: "block", fontSize: 11, color: NJIM.textMuted, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {customer.customerCode}
              {customer.customerCategory ? ` · ${customer.customerCategory}` : ""}
            </Typography>
          </Box>
          {outstanding > 0 && (
            <Tooltip title={`${t("codSalesRep.customerOutstanding")}: ${formatCodAmount(outstanding, customer.currency)}`}>
              <Box component="span" sx={{ ...countDotSx, bgcolor: NJIM.dangerWash, color: NJIM.danger, px: 1 }}>
                {compactAmount(outstanding)}
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  /* Desktop: sticky side panel, same pattern as the Sales Manager team list. */
  const panel = (
    <Box
      sx={{
        display: { xs: "none", lg: "flex" },
        flexDirection: "column",
        position: "sticky",
        top: 88,
        maxHeight: "calc(100vh - 112px)",
        borderRadius: "16px",
        border: `1px solid ${NJIM.border}`,
        background: NJIM.white,
        boxShadow: NJIM.elevation,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, pb: 1.5, borderBottom: `1px solid ${NJIM.border}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              bgcolor: NJIM.mintWash,
              color: NJIM.ink,
              flexShrink: 0,
            }}
          >
            <PersonSearchRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: NJIM.ink, lineHeight: 1.3 }}>
              {t("codSalesRep.selectCustomer")}
            </Typography>
            <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, lineHeight: 1.35 }}>
              {t("codSalesRep.selectCustomerHelper")}
            </Typography>
          </Box>
          {customerResults.length > 0 && (
            <Chip
              size="small"
              label={customerResults.length}
              sx={{ height: 22, fontWeight: 700, bgcolor: NJIM.mintWash, color: NJIM.ink }}
            />
          )}
        </Box>
        {accessAlerts}
        <SearchField
          value={customerSearch}
          onChange={setCustomerSearch}
          placeholder={t("codSalesRep.searchCustomers")}
          disabled={customerSearchDisabled}
        />
        <Box sx={{ mt: 1.25 }}>{debtToggle}</Box>
      </Box>

      <Box
        component="ul"
        sx={{
          m: 0,
          p: 1,
          listStyle: "none",
          overflowY: "auto",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {!customerSearch.trim() && (
          <Box component="li">
            <Box
              component="button"
              type="button"
              aria-pressed={!selected}
              onClick={() => onSelect(null)}
              sx={customerRowSx(!selected)}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: !selected ? NJIM.mint : NJIM.ink,
                  color: NJIM.white,
                  flexShrink: 0,
                }}
              >
                <GroupsRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography component="span" sx={{ flex: 1, textAlign: "start", fontSize: 13, fontWeight: !selected ? 700 : 600, color: NJIM.textPrimary }}>
                {t("codSalesRep.allCustomers")}
              </Typography>
            </Box>
          </Box>
        )}
        {loadingCustomers && customerResults.length === 0 && (
          <Box component="li" sx={{ p: 1 }}>
            <CardSkeleton count={3} />
          </Box>
        )}
        {listRows.map(renderRow)}
        {!loadingCustomers && customerSearch.trim() && visibleResults.length === 0 && (
          <Box component="li" sx={{ py: 3, px: 2, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13, color: NJIM.textMuted }}>
              {repCustomerAccess === "noneAssigned"
                ? t("codSalesRep.noCustomersAssigned")
                : t("codSalesRep.noSearchMatches")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  /* Tablet / mobile: searchable dropdown (server search on type). */
  const dropdownOptions = selected
    ? [selected, ...visibleResults]
    : visibleResults;

  const dropdown = (
    <Box sx={{ display: { xs: "block", lg: "none" }, ...glassCardSx, p: 2, mb: 0 }}>
      {accessAlerts}
      <Autocomplete
        options={dropdownOptions}
        value={selected}
        onChange={(_, value) => onSelect(value ?? null)}
        inputValue={customerSearch}
        onInputChange={(_, value, reason) => {
          if (reason === "input" || reason === "clear") setCustomerSearch(value);
        }}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.customerName || option.customerCode}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={loadingCustomers}
        disabled={customerSearchDisabled}
        noOptionsText={t("codSalesRep.noSearchMatches")}
        renderOption={(props, option) => {
          const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string }; // eslint-disable-line react/prop-types
          const outstanding = resolveCustomerOutstanding(outstandingLookup, option);
          return (
            <Box component="li" key={key} {...rest} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <CodInitialsAvatar name={option.customerName} size={30} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {option.customerName}
                </Typography>
                <Typography sx={{ fontSize: 11, color: NJIM.textMuted }}>{option.customerCode}</Typography>
              </Box>
              {outstanding > 0 && (
                <Box component="span" sx={{ ...countDotSx, bgcolor: NJIM.dangerWash, color: NJIM.danger, px: 1 }}>
                  {compactAmount(outstanding)}
                </Box>
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("codSalesRep.searchCustomers")}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <PersonSearchRoundedIcon sx={{ fontSize: 20, color: NJIM.textSecondary, marginInlineStart: "4px" }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{
          "& .MuiOutlinedInput-root": { minHeight: 44, borderRadius: "10px", background: NJIM.white },
        }}
      />
      <Box sx={{ mt: 1 }}>{debtToggle}</Box>
    </Box>
  );

  return (
    <>
      {panel}
      {dropdown}
    </>
  );
};

const customerRowSx = (selected: boolean) =>
  ({
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    width: "100%",
    boxSizing: "border-box",
    minHeight: 52,
    px: 1.25,
    py: 0.75,
    borderRadius: "10px",
    border: "1px solid transparent",
    borderColor: selected ? NJIM.mint : "transparent",
    background: selected ? NJIM.mintWash : "transparent",
    cursor: "pointer",
    font: "inherit",
    transition: `background-color 150ms ${NJIM.easeOut}, border-color 150ms ${NJIM.easeOut}`,
    "&:hover": { background: selected ? NJIM.mintWash : NJIM.muted },
    "&:focus-visible": { outline: `2px solid ${NJIM.mint}`, outlineOffset: 1 },
  }) as const;

const countDotSx = {
  minWidth: 20,
  height: 20,
  px: 0.75,
  borderRadius: "999px",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: "20px",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
  boxSizing: "border-box",
  flexShrink: 0,
} as const;

export default CodCustomerPicker;
